import { useEffect, useRef } from "react";

import { useI18n } from "../i18n.js";

/**
 * Il grafico a barre delle emissioni, portato da `EmissionBarChart.qml`.
 *
 * Ogni barra e' un sotto-periodo, e porta con se' quattro cose:
 *
 *  - l'**altezza** e' la massa emessa;
 *  - il **colore** e' la classe di qualita' del dato in quel sotto-periodo;
 *  - la **banda chiara** attorno alla cima e' l'incertezza dichiarata;
 *  - il **tratteggio** a tutta altezza vuol dire che in quel sotto-periodo non
 *    ci sono letture.
 *
 * L'ultimo punto e' quello che conta di piu': un sotto-periodo senza dati non
 * e' un sotto-periodo a emissione zero, e disegnarlo come una barra schiacciata
 * a terra direbbe la seconda cosa. Su un documento che il cliente firma sono
 * due affermazioni diverse.
 */

export interface BreakdownRow {
  period: string;
  totalMassKg: number;
  uncertaintyPct: number;
  qualityClass: string;
  readingCount?: number;
  status?: string;
}

export function formatMass(kg: number): string {
  const v = Number(kg) || 0;
  if (v >= 1) return `${v.toFixed(2)} kg`;
  const g = v * 1000;
  if (g >= 1) return `${g.toFixed(1)} g`;
  return `${(g * 1000).toFixed(1)} mg`;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** L'etichetta sull'asse X si ricava dalla FORMA della stringa del periodo,
 *  che e' come fa il `.qml`: `YYYY-MM` un mese, `YYYY-MM-DD` un giorno,
 *  `YYYY-MM-DDTHH` un'ora. */
export function periodLabel(p: string): string {
  if (!p) return "";
  if (p.length === 7) return MONTHS[Number(p.slice(5, 7)) - 1] ?? p;
  if (p.length === 10) return `${p.slice(8, 10)}/${p.slice(5, 7)}`;
  if (p.includes("T")) return `${p.slice(11, 13)}:00`;
  return p;
}

function qualityRgba(qc: string, alpha: number): string {
  if (qc === "Good") return `rgba(34,197,94,${alpha})`;
  if (qc === "Moderate") return `rgba(234,179,8,${alpha})`;
  return `rgba(239,68,68,${alpha})`;
}

export function EmissionBarChart({
  breakdown,
  compact = false,
}: {
  breakdown: BreakdownRow[];
  compact?: boolean;
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;

    const draw = () => {
      const w = parent.clientWidth;
      const h = parent.clientHeight;
      if (w <= 0 || h <= 0) return;
      const dpr = globalThis.devicePixelRatio || 1;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      paint(ctx, w, h, breakdown, compact);
    };

    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [breakdown, compact]);

  return (
    <div className="barchart">
      <canvas ref={ref} />
      {breakdown.length === 0 && <span className="barchart__empty">{t("msg.noEmissionPeriod")}</span>}
    </div>
  );
}

function paint(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  data: BreakdownRow[],
  compact: boolean,
) {
  const n = data.length;
  if (n === 0) return;

  const labelH = 28;
  const labelW = compact ? 0 : 58;
  const padTop = 8;
  const padRight = 8;
  const plotX = labelW;
  const plotY = padTop;
  const plotW = width - labelW - padRight;
  const plotH = height - labelH - padTop;

  // La scala verticale arriva fino al MASSIMO dell'incertezza, non della
  // massa: altrimenti la banda esce dal grafico proprio sulla barra piu' alta.
  let maxVal = 1e-9;
  for (const d of data) {
    const m = Number(d.totalMassKg) || 0;
    const u = Number(d.uncertaintyPct) || 0;
    maxVal = Math.max(maxVal, m * (1 + u / 100));
  }
  if (maxVal <= 0) maxVal = 1;

  const yOf = (v: number) => plotY + plotH - (v / maxVal) * plotH;

  ctx.strokeStyle = "rgba(255,255,255,0.06)";
  ctx.setLineDash([3, 3]);
  ctx.lineWidth = 1;
  for (let g = 1; g <= 4; g++) {
    const gy = plotY + plotH - (g / 4) * plotH;
    ctx.beginPath();
    ctx.moveTo(plotX, gy);
    ctx.lineTo(plotX + plotW, gy);
    ctx.stroke();
  }
  ctx.setLineDash([]);

  const slotW = plotW / n;
  const barW = Math.max(2, slotW * 0.65);

  data.forEach((d, i) => {
    const mass = Number(d.totalMassKg) || 0;
    const unc = Number(d.uncertaintyPct) || 0;
    const qc = d.qualityClass || "Poor";
    const status = d.status ?? (mass > 0 ? "complete" : "no_data");
    const cx = plotX + i * slotW + (slotW - barW) / 2;
    const bH = Math.max(2, (mass / maxVal) * plotH);
    const bY = plotY + plotH - bH;

    if (status === "no_data") {
      ctx.save();
      ctx.beginPath();
      ctx.rect(cx, plotY, barW, plotH);
      ctx.clip();
      ctx.strokeStyle = "rgba(160,160,160,0.35)";
      ctx.lineWidth = 1.5;
      for (let s = -plotH; s < barW + plotH; s += 6) {
        ctx.beginPath();
        ctx.moveTo(cx + s, plotY + plotH);
        ctx.lineTo(cx + s + plotH, plotY);
        ctx.stroke();
      }
      ctx.restore();
      ctx.strokeStyle = "rgba(160,160,160,0.3)";
      ctx.lineWidth = 1;
      ctx.strokeRect(cx, plotY, barW, plotH);
    } else {
      if (mass > 0 && unc > 0) {
        const delta = (mass * unc) / 100;
        const highY = yOf(Math.min(maxVal, mass + delta));
        const lowY = yOf(Math.max(0, mass - delta));
        ctx.fillStyle = qualityRgba(qc, 0.12);
        ctx.fillRect(cx, highY, barW, lowY - highY);
      }
      ctx.fillStyle = qualityRgba(qc, 0.85);
      ctx.beginPath();
      ctx.moveTo(cx + 2, bY);
      ctx.arcTo(cx + barW, bY, cx + barW, bY + bH, 2);
      ctx.arcTo(cx + barW, bY + bH, cx, bY + bH, 0);
      ctx.arcTo(cx, bY + bH, cx, bY, 0);
      ctx.arcTo(cx, bY, cx + barW, bY, 2);
      ctx.closePath();
      ctx.fill();
    }

    ctx.fillStyle = "rgba(180,180,180,0.7)";
    ctx.font = "9px monospace";
    ctx.textAlign = "center";
    ctx.fillText(periodLabel(d.period), cx + barW / 2, plotY + plotH + 18);
  });

  if (!compact) {
    ctx.textAlign = "right";
    ctx.fillStyle = "rgba(180,180,180,0.65)";
    ctx.font = "9px monospace";
    for (let k = 0; k <= 4; k++) {
      const v = (k / 4) * maxVal;
      const ky = plotY + plotH - (k / 4) * plotH;
      ctx.fillText(formatMass(v), plotX - 4, ky + 4);
    }
  }
}
